import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import { Container, Lines, Reveal } from '@/components/ui'
import { useSlotT } from '@/cms/useSlotT'
import { buildLocalePath, type Locale } from '@/utils/localePath'

/**
 * SmartCindyTeaser — banner for SmartCindy, the AI further-study &
 * career advisor.
 *
 * WHERE IT RENDERS
 * ----------------
 * Two surfaces share this one component:
 *   1. The homepage, as the `smartcindy` section in the CMS section
 *      registry (src/content/sections.ts).
 *   2. `/products`, as the featured-product spotlight that sits directly
 *      under the page header — the same slot the My Harbour teaser
 *      occupies, because SmartCindy is a product in its own right.
 *
 * Both render the identical block, so the two surfaces can never drift
 * apart visually. The only per-surface difference is the section's
 * background tone, which `tone` controls so the page keeps its
 * alternating surface / surface-2 rhythm.
 *
 * WHY A DEDICATED BLOCK RATHER THAN A CARD IN ProductsGrid
 * --------------------------------------------------------
 * ProductsGrid used to list `ai-advisor` among the featured products, and
 * that card was where the "view details" link lived. 2026-11-04: the grid
 * slot went to the new 國民教育數碼系統 (national-education took the
 * featured flag), so this banner is now SmartCindy's only homepage
 * presence. But a product that
 * sells itself on a promise — "24/7, any language, grounded answers" —
 * needs more than a card with three bullet points to be understood.
 *
 * The banner carries the promise in full: the product's own hero image, the
 * three headline capabilities, and a direct route into the detail page.
 *
 * THE IMAGE
 * ---------
 * `smart-cindy-homepage-hero.jpg` lives in public/products/smart-cindy/ and
 * is served by Vite from the URL literal (not bundled through src/assets),
 * so the same pattern as the rest of the site's product imagery applies:
 * the file is referenced by absolute path and the prerender pass can
 * resolve it without touching Node's bare-path .png resolution.
 *
 * The image carries the product's own Chinese/English headline artwork, so
 * it is decorative-plus-informative rather than a neutral illustration. It
 * is marked `loading="lazy"` and sits below the fold on mobile, where the
 * copy above it carries the message instead.
 */
/**
 * Tailwind only emits classes it can see as literal strings, so the two
 * tones are spelled out here rather than interpolated (`bg-${tone}`
 * would compile to nothing).
 */
const TONE_CLASS: Record<'surface' | 'surface-2', string> = {
  surface: 'bg-surface',
  'surface-2': 'bg-surface-2',
}

export default function SmartCindyTeaser({
  tone = 'surface-2',
}: {
  /**
   * Background tone of the wrapping section. The homepage renders this
   * block right after the products grid (`bg-surface`), so it takes
   * `surface-2` there; on `/products` the block follows the page header
   * and precedes the My Harbour teaser (`bg-surface-2`), so it takes
   * `surface` to keep the page's alternating rhythm.
   */
  tone?: 'surface' | 'surface-2'
  /**
   * The homepage section registry hands every section component its CMS
   * settings. This block has no operator-tunable settings, so the prop
   * is accepted and ignored — but it has to be declared, or the
   * component would not be assignable to the registry's
   * `ComponentType<{ settings?: SectionSettings }>`.
   */
  settings?: Record<string, unknown>
}) {
  const slotT = useSlotT('home')
  const { t, i18n } = useTranslation()
  // Every public URL carries a locale prefix (/zh-Hant, /zh-Hans, /en).
  // A bare `/products/smart-cindy` link would be bounced by the
  // bare-path redirect map into the DEFAULT locale, so a visitor on
  // /en/ would be thrown back into Chinese. Build the prefixed path.
  const locale = (i18n.language || 'zh-Hant') as Locale

  return (
    <section
      className={`py-section-y px-section-x ${TONE_CLASS[tone]}`}
      aria-labelledby="smartcindy-teaser-title"
    >
      <Container>
        <Reveal>
          {/* A rounded card rather than a full-bleed band: the homepage
              alternates wide sections with card-shaped ones, and this
              block's job is to read as a product poster, not as another
              editorial section. */}
          <div className="relative overflow-hidden rounded-yada-2xl bg-gradient-to-br from-[#0d332b] via-[#1f9a78] to-[#34ad88] shadow-yada-lg">
            <div className="grid lg:grid-cols-2 items-center">
              {/* Copy */}
              <div className="relative z-10 p-8 md:p-12">
                <span className="inline-block bg-white/15 text-[#8ee0bc] px-3 py-1 rounded-yada-full text-xs font-semibold mb-4 tracking-wide">
                  {slotT('home.smartCindyTeaser.badge')}
                </span>
                <h2
                  id="smartcindy-teaser-title"
                  className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-3 leading-tight"
                >
                  {slotT('home.smartCindyTeaser.title')}
                </h2>
                <p className="text-base md:text-lg text-white/90 mb-6 leading-relaxed max-w-xl">
                  {slotT('home.smartCindyTeaser.description')}
                </p>
                <ul className="flex flex-wrap gap-2 mb-7">
                  {(['24/7', 'rag', 'grounded'] as const).map((key) => (
                    <li
                      key={key}
                      className="text-xs md:text-sm font-semibold px-3.5 py-1.5 rounded-yada-full bg-white/12 border border-white/25 text-white"
                    >
                      {slotT(`home.smartCindyTeaser.chips.${key}`)}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={buildLocalePath('/products/smart-cindy', locale)}
                    className="inline-flex items-center justify-center gap-2 bg-white text-[#0d332b] px-6 py-3 rounded-yada-full font-bold hover:bg-[#e8f8ef] transition-colors focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                  >
                    <span>{slotT('home.smartCindyTeaser.cta')}</span>
                    <Icon icon="lucide:arrow-right" />
                  </Link>
                  <Link
                    to={buildLocalePath('/contact', locale)}
                    className="inline-flex items-center justify-center gap-2 text-white border-2 border-white/55 px-6 py-3 rounded-yada-full font-semibold hover:bg-white/10 transition-colors focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                  >
                    <Lines text={t('common.getInTouch')} />
                  </Link>
                </div>
              </div>

              {/* Hero image */}
              <div className="relative lg:order-2">
                <img
                  src="/products/smart-cindy/smart-cindy-homepage-hero.jpg"
                  alt={slotT('home.smartCindyTeaser.imageAlt')}
                  loading="lazy"
                  decoding="async"
                  width={2048}
                  height={1152}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  )
}
